import React from 'react';
import type { Draft } from './useUnderwritePrimeXQuote';
import type { DescribedRow } from './describedRows';
import { IDLE, type DraftRate } from './useDraftRate';
import { NO_STAGED_FIELDS, type StagedFieldIndex } from './stagedFields';

/**
 * `Draft` plus the proposed rate, which comes from a different hook
 * (`useDraftRate`) — only the provider has both `draftState` and
 * `committedState` to price, which is why the composition lives here and
 * not in `Draft` itself.
 */
export type DraftContextValue = Draft & {
  readonly rate: DraftRate;
  /**
   * `rows` named the way the quote grid names them, with their premium impact.
   *
   * Computed once by the view and shared: the banner groups them into section
   * chips, and the chat hand-off card lists them. Two presentations of one
   * producer — deriving them twice is how the section a row is filed under
   * could come to disagree between the panel and the page.
   */
  readonly describedRows: readonly DescribedRow[];
  /**
   * The same rows, addressed by the field they sit on.
   *
   * The banner reads `describedRows` in log order; a control in the grid has to
   * ask the opposite question — "is one of these mine". Indexed once beside the
   * rows it indexes, so the two cannot be built from different inputs.
   */
  readonly stagedFields: StagedFieldIndex;
};

/**
 * The fallback is a real, inert `DraftContextValue` — components rendered
 * outside the provider (MTE shares this tree) read zero rows and an idle
 * rate rather than crashing. No `undefined as never` casts: the context's
 * type is what it actually holds.
 */
const DISABLED: DraftContextValue = {
  rows: [],
  counts: { total: 0, operator: 0, assistant: 0 },
  accept: () => undefined,
  reject: () => undefined,
  acceptAll: () => undefined,
  rejectAll: () => undefined,
  rate: IDLE,
  describedRows: [],
  stagedFields: NO_STAGED_FIELDS,
};

const DraftContext = React.createContext<DraftContextValue>(DISABLED);

export const DraftProvider = DraftContext.Provider;

export const useDraft = (): DraftContextValue => React.useContext(DraftContext);
